import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, LocalShipping, FilterList } from '@mui/icons-material';
import { transportationJobService } from '../services/transportationJobService';
import { formatCurrency } from '../components/CurrencyTextField';
import { formatDateOnly } from '../utils/dateUtils';
import { useAuth } from '../hooks/useAuth';
import {
  driverDisplayName,
  formatDateInput,
  transportJobTypeLabel,
  transportStatusMeta,
  TRANSPORT_STATUSES,
} from '../constants/transportation';
import usePersistedListFilters from '../hooks/usePersistedListFilters';

const LIST_FILTERS_DEFAULTS = {
  search: '',
  status: '',
  page: 0
};

const TransportationJobs = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const admin = typeof isAdmin === 'function' && isAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    values: listFilters,
    setField: setListFilter,
    setValues: setListFilters,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_transportation_list_filters', LIST_FILTERS_DEFAULTS);
  const search = listFilters.search;
  const setSearch = useCallback((v) => setListFilter('search', v), [setListFilter]);
  const status = listFilters.status;
  const setStatus = useCallback((v) => setListFilter('status', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    setSearchParams({}, { replace: true });
  }, [clearListFilters, setSearchParams]);

  const seededFromUrlRef = useRef(false);
  useEffect(() => {
    if (seededFromUrlRef.current) return;
    seededFromUrlRef.current = true;
    const hasUrlFilters =
      searchParams.has('q') || searchParams.has('status') || searchParams.has('page');
    if (!hasUrlFilters) return;
    setListFilters({
      search: searchParams.get('q') || '',
      status: searchParams.get('status') || '',
      page: Number(searchParams.get('page') || 0) || 0
    });
  }, [searchParams, setListFilters]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await transportationJobService.getAll({
        search: search || undefined,
        status: status || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: 'created_at',
        direction: 'desc',
      });
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load transportation jobs');
    } finally {
      setLoading(false);
    }
  }, [search, status, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    if (status) next.status = status;
    if (page) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [search, status, page, setSearchParams]);

  const handleDelete = async (row) => {
    if (row.status === 'complete') return;
    const label = row.identifier || `#${row.id}`;
    if (!window.confirm(`Delete transportation job ${label}? This cannot be undone.`)) return;
    try {
      await transportationJobService.delete(row.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete job');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <LocalShipping color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Transportation Jobs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/transportation/new')}
        >
          New Job
        </Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setPage(0);
              setStatus(e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {TRANSPORT_STATUSES.map((s) => (
              <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear filters
          </Button>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {total === 0 ? 'No jobs match the current filters.' : `${total} job${total === 1 ? '' : 's'}`}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Pickup</TableCell>
              <TableCell>Drop Off</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total</TableCell>
              {admin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={admin ? 9 : 8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={admin ? 9 : 8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No transportation jobs yet. Create one to send a driver a pickup/delivery link.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const meta = transportStatusMeta(row.status);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/transportation/${row.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.identifier || `#${row.id}`}</TableCell>
                    <TableCell>{row.work_order_identifier || '—'}</TableCell>
                    <TableCell>{driverDisplayName(row) || '—'}</TableCell>
                    <TableCell>{transportJobTypeLabel(row.job_type)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.pickup_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateOnly(formatDateInput(row.pickup_date)) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.dropoff_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                        {row.dropoff_address || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={meta.label} color={meta.color} />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                    {admin && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {row.status !== 'complete' && (
                          <IconButton
                            size="small"
                            color="error"
                            title="Delete job"
                            onClick={() => handleDelete(row)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default TransportationJobs;
